/* ------------------------------------------------------------------------
  Copyright (c) 2016, VoiceVibes, Inc

  VoiceVibes, Inc - PROPRIETARY/CONFIDENTIAL
  All contents of this file are proprietary and confidential. No part of
  any of the contents of this file may be disclosed in any manner to
  a third party without the prior written consent of VoiceVibes, Inc.
  Unauthorized copying of this file, via any medium is strictly prohibited.
----------------------------------------------------------------------------- */

<template>
  <div
    v-if="basePageLoaded"
    class="row"
  >
    <div
      class="col-sm-10 col-sm-offset-1"
      aria-labelledby="zoomPage"
    >
      <h1 id="zoomPage">
        Import Your Zoom Recordings
      </h1>
      <div class="zoom-help">
        For help installing or using the VoiceVibes Zoom integration,
        <a
          href="https://www.myvoicevibes.com/zoom-help.html"
          target="_blank"
        >click here</a>.
        <br><br>
      </div>
      <div v-if="loading">
        <h2>
          <svg
            class="icon-refresh rotate large-spinner"
          >
            <use xlink:href="/static/svg-symbols.svg#icon-refresh" />
          </svg>
          Recordings loading from Zoom
        </h2>
      </div>
      <div v-else>
        <div v-if="!recordings || !recordings.length">
          No Zoom Recordings were found. If you're certain you have cloud recordings saved to
          Zoom, or if an error message is shown on this page, please click reload on your browser
          to try again.<br><br>You can also
          <a :href="zoomUrl">
            click here
          </a>
          to reset the Zoom integration permissions.
        </div>
        <div v-if="showWarning">
          You appear to have a large number of Zoom meetings in your account. The
          VoiceVibes Zoom Integration only supports the use of up to 100 meetings within a 15-day
          period. You appear to have exceeded this limit. As a result, the list of meetings
          shown below may be incomplete. If you are unable to locate a specific meeting below, you
          will need to download it directly from Zoom and upload it via VoiceVibes' Upload tab on
          the RECORD page.
          <br><br>
        </div>
        <div
          v-for="(rec, count) in recordings"
          class="zRecording"
        >
          <h4>{{ rec.topic }}</h4>
          Start Time: {{ rec.start_time | date }}
          <br>
          <span
            v-if="activeMeeting != rec"
            class="viewRecording"
            @click.prevent="setActiveMeeting(rec)"
          >
            <svg
              class="icon-chevrons-down"
            >
              <use xlink:href="/static/svg-symbols.svg#icon-chevrons-down" />
            </svg>
            <a
              href="#"
              @click.prevent
            >
              Recording Details
            </a>
          </span>
          <form
            v-if="activeMeeting == rec"
            class="rDetails"
            @submit.prevent="upload"
          >
            <span v-if="!singleRecording">
              <br>
              <strong>Step 1:</strong> Select a recording to process
              <br>
            </span>
            <div
              v-for="(ar, idx) in activeRecordings"
            >
              <input
                :id="'recording' + idx"
                v-model="downloadUrlIdx"
                type="radio"
                name="ar"
                :style="singleRecording ? 'display:none' : ''"
                :value="idx"
              >
              <label
                :for="'recording' + idx"
                style="font-weight: normal;"
              >
                <span v-if="!singleRecording">
                  Recording {{ idx + 1 }},
                </span>
                Est. Duration: {{ ar.duration | time }}
              </label>
              <a
                :href="ar.play_url"
                target="_blank"
                class="zoomPlayHref"
              >
                <svg class="svgicon icon-soundplay">
                  <use xlink:href="/static/svg-symbols.svg#icon-soundplay" />
                </svg>
                <span class="playText">
                  Play
                </span>
              </a>
              <br>
            </div>
            <br>
            <strong>Step {{ singleRecording ? '1' : '2' }}:</strong>
            Use the play button to find the correct Start and End values
            to remove any silence and parts of the recording where others are talking. Setting
            start and end values below helps assure your recording metrics are accurate.
            Max trimmed length: 15 minutes.
            <br>
            <upload-trimming
              :trim="formValues.trim"
              :count="count + ''"
            />
            <strong>Step {{ singleRecording ? '2' : '3' }}:</strong>
            Name your new recording
            <span v-if="speakerGroups.length > 1">
              and select {{ assignmentsEnabled ? 'an assignment or private recording' : 'which group to use' }}.
            </span><br>
            <assignment-select
              v-if="showAssignmentSelect"
              @assignmentChosen="assignmentChosen"
            />

            <div
              class="form-group"
              style="margin-top: 5px;"
            >
              <label for="zoomTitle">
                Title
              </label>
              <input
                id="zoomTitle"
                v-model.trim="formValues.title"
                type="text"
                class="form-control"
                placeholder="title"
                name="title"
                required
                maxlength="50"
              >
            </div>

            <speaker-select
              v-if="showAlgorithmSelect"
              @algorithmChosen="algorithmChosen"
            />

            <assignment-sharing
              v-if="assignmentsEnabled && recordingType !== 'private'"
              ref-num="2"
              @sharingChanged="sharingChanged"
            />

            <div
              v-if="speakerGroups.length > 1 && !assignmentsEnabled"
              class="form-group"
            >
              <label for="groupSelector4">
                Group
              </label>
              <select
                id="groupSelector4"
                v-model="selectedGroup"
                class="form-control"
              >
                <option
                  v-for="group in speakerGroups"
                  :value="group"
                >
                  {{ group.name }}
                </option>
              </select>
            </div>
            <loading-button
              type="submit"
              :loading="uploading"
            >
              Step {{ singleRecording ? '3' : '4' }}: Process Recording
            </loading-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
  .viewRecording {
    display: block;
    text-align: center;
  }
  .playText {
    color: #F26724 !important;
    text-decoration: none !important;
    font-size: 12pt;
  }
  .zoomPlayHref {
    text-decoration: none !important;
    margin-left:40px;
  }

  .svgicon {
    display: inline-block;
    stroke-width: 0;
    stroke: currentColor;
    fill: currentColor;
  }
  .icon-chevrons-down {
    height: 14px; width: 14px;
  }
  .icon-soundplay {
    width: 23px; height: 23px;
    position: relative;
    top: 6px;
  }
  .zRecording {
    padding: 8px;
    border-radius: 15px;
    border: 1px solid #F26724;
    margin-bottom: 8px;
  }
  #zoomTitle {
    max-width: 500px;
  }
</style>

<script>
import BasePage from './BasePage';
import UploadTrimming from '../forms/UploadTrimming';
import moment from 'moment';
import recordingsService from '../../services/recordings';
import AssignmentRecording from '../specials/AssignmentRecording';
import AssignmentSelect from '../specials/AssignmentSelect';
import AssignmentSharing from '../specials/AssignmentSharing';
import SpeakerSelect from '../specials/SpeakerSelect';

export default {
  components: {AssignmentSelect, UploadTrimming, AssignmentSharing, SpeakerSelect},
  mixins: [BasePage, AssignmentRecording],
  data() {
    return {
      loading: true,
      uploading: false,
      activeMeeting: null,
      activeRecordings: [],
      downloadUrlIdx: 0,
      formValues: {
        title: '',
        submitted: false,
        feedbackRequested: false,
        source: 'zoom form',
        trim: {
          from: 0,
          to: -1
        },
      },
    };
  },
  computed: {
    singleRecording: {
      cache: false,
      get() {
        return this.activeRecordings.length === 1;
      }
    },
    zoomUrl() {
      return this.$store.getters.appConfig.zoomAuth;
    },
    speakerGroups() {
      let speakerGroups = this._.filter(this.$store.getters.groups, group => {
        return this._.includes(group.roles, 'speaker') && group.membershipStatus === 'active';
      });
      speakerGroups = this._.sortBy(
        speakerGroups, g => -new Date(g.createdDate).getTime()
      );

      // Set default selectedGroup
      if (!this.selectedGroup && speakerGroups.length) {
        this.selectedGroup = speakerGroups[0];
      }

      return speakerGroups;
    },
    showWarning: {
      cache: false,
      get() {
        let response = this.$store.getters.zoomResponse;
        return response.recordingsDropped;
      }
    },
    recordings: {
      cache: false,
      get() {
        let allRecordings = this.$store.getters.zoomRecordings;

        // Discard any new pending recordings
        allRecordings = this._.filter(
          allRecordings,
          (r) => !!(r.recording_files && r.recording_files.length && r.recording_files[0].download_url)
        );
        return allRecordings;
      }
    },
    org() {
      return this.$store.getters.org;
    },
    videoEnabled() {
      return (this.org && typeof this.org.settings !== 'undefined' && this.org.settings.Video);
    },
  },
  created() {
    const promises = [this.$store.dispatch('getZoomRecordings')];
    if (this.assignmentsEnabled) {
      promises.push(this.$store.dispatch('getAssignedAssignments'));
    }

    window.Promise.all(promises)
      .then(() => {
        this.populateAssignmentAndTitle();
        this.loading = false;
      })
      .catch(() => {
        window.location.href = this.zoomUrl;
      });
  },
  methods: {
    reset() {
      this.uploading = false;
      this.activeMeeting = null;
      this.activeRecordings = [];
      this.downloadUrlIdx = 0;
      this.formValues.title = '';
      this.formValues.submitted = false;
      this.formValues.feedbackRequested = false;
      this.formValues.trim = {
        from: 0,
        to: -1
      };
      this.formValues.source = 'zoom form';
    },
    upload() {
      this.uploading = true;

      // Check for video
      if (this.videoEnabled) {
        this.formValues.isVideo = true;
      }

      const formData = this.getFormData();
      recordingsService.post(formData)
        .then(recording => {
          const recordingId = recording.recordingId;
          this.addRecordingToAssignment(recordingId);
          return this.$store.dispatch('listRecordings')
            .then(() => {
              this.reset();
              this.recordingUploaded(recordingId, formData);
            });
        })
        .catch(() => {
          this.$store.dispatch('notify', {
            type: 'error',
            subject: 'Zoom Upload Error',
            text: `Error processing the Zoom recording. Please click reload on your browser and
              try to process the recording again.`
          });
        })
        .finally(() => {
          this.uploading = false;
        });

    },
    setActiveMeeting(rec) {
      this.activeMeeting = rec;

      // We need to avoid vuex mutation warnings
      const recordings = _.cloneDeep(rec.recording_files);

      const recs = _.filter(recordings, (r) => r.recording_type === 'audio_only');
      for (let i=0; i<recordings.length; i++) {
        const videoVersion = rec.recording_files[i];
        if (videoVersion.recording_type === 'audio_only' || videoVersion.recording_type === 'chat_file') continue;

        const audioVersion = _.find(recs, (r) => {
          return (r.recording_start == videoVersion.recording_start &&
          r.recording_end == videoVersion.recording_end);
        });
        audioVersion.play_url = videoVersion.play_url;
        if (this.videoEnabled) {
          audioVersion.download_url = videoVersion.download_url;
        }

        // Also set the duration
        audioVersion.duration =
          moment(audioVersion.recording_end)
            .diff(moment(audioVersion.recording_start)) / 1000;
      }
      this.downloadUrlIdx = 0;
      this.formValues.title = (this.assignment && this.assignment.name) ? this.getDefaultTitle() : rec.topic;
      this.activeRecordings = recs;
    }
  }
};
</script>
